'use client';

// paypal-wallet: express PayPal Smart Button for /comix/unlock. One tap opens PayPal — no
// sheet of ours first. The buyer's PayPal email / ship-to / phone are validated on OUR
// server after approval (same rules + Lulu check as the Apple/Google Pay form) and only
// then captured. Renders nothing unless the `wallet_checkout_paypal` flag is on for this
// visitor (GET /api/comix/wallet/config → paypal: { clientId, env }).

import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from '@/lib/amplitude';
import { markCheckoutStarted } from '@/lib/comix/use-pending-approvals';
import { formatUsPhone, LULU_ADDRESS_LIMITS } from '@/lib/wallet/validate-contact';
import { STANDARD_INNER_PAGES, type BookPageCount } from '@/lib/comix/book-length';
import { walletMetaEvent } from './WalletExpressPay';

type Product = 'digital' | 'printed';
interface Setup {
  clientId: string;
  env: 'live' | 'sandbox';
}
interface AttemptView {
  attemptId: string;
  phase: string;
  locked: boolean;
  product: Product | 'bag';
  variantId?: string;
  subtotalCents: number;
  totalCents: number;
  orderName: string | null;
  sitePurchaseEvent?: boolean;
  declineReason?: string | null;
  runIds?: string[];
  items?: { variantId: string; quantity: number; unitCents: number; product: string }[];
}

const WATCHED = ['authorizing', 'held', 'capturing', 'paid_order_pending', 'releasing'];

async function post(url: string, body: unknown) {
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const json = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, json };
}

export default function PaypalExpressButton({
  runId,
  sessionId,
  product,
  quantity = 1,
  pageCount = STANDARD_INNER_PAGES,
  className = 'mt-3',
  onLockedChange,
  onResolvingChange,
  bag,
}: {
  /** Single book: its run. Bag mode: unused (the cart's lines carry their runs). */
  runId: string | null | undefined;
  sessionId: string | null | undefined;
  product: Product;
  quantity?: number;
  /** Single-book mode: which length (12/28 inner pages) to quote/charge. Bag mode ignores
   *  this — the cart's own lines (already extended-aware) decide. */
  pageCount?: BookPageCount;
  className?: string;
  onLockedChange?: (locked: boolean) => void;
  onResolvingChange?: (resolving: boolean) => void;
  /** paypal-wallet (bag): pay for the whole cart. Mirrors WalletExpressPay's `bag` prop —
   *  `signature` changes whenever lines/quantities/totals change (forces a fresh quote). */
  bag?: { signature: string; hasPrinted: boolean };
}) {
  const [setup, setSetup] = useState<Setup | null>(null);
  const [attempt, setAttempt] = useState<AttemptView | null>(null);
  const [needsPhone, setNeedsPhone] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resolving, setResolving] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // PayPal's callbacks are bound when the button renders — read the live selection from refs.
  const productRef = useRef(product);
  productRef.current = product;
  const quantityRef = useRef(quantity);
  quantityRef.current = quantity;
  const pageCountRef = useRef(pageCount);
  pageCountRef.current = pageCount;
  const attemptIdRef = useRef<string | null>(null);

  const loadConfig = useCallback(async () => {
    if (!sessionId) return;
    const cfg = await fetch(`/api/comix/wallet/config?sessionId=${encodeURIComponent(sessionId)}`, { cache: 'no-store' })
      .then((r) => r.json())
      .catch(() => null);
    setSetup(cfg?.paypal && typeof cfg.paypal.clientId === 'string' ? cfg.paypal : null);
  }, [sessionId]);

  const isBag = !!bag;
  useEffect(() => {
    if (!runId && !isBag || !sessionId) return;
    void loadConfig();
    setResolving(true);
    // Restore an in-flight PayPal payment after a reload so the page stays locked. Bag
    // attempts skip this (same simplification WalletExpressPay makes) — they're tied to the
    // cart, not a single run, and there's no single `runId` to key the lookup on.
    if (isBag || !runId) {
      setResolving(false);
      return;
    }
    fetch(`/api/comix/wallet/active?runId=${encodeURIComponent(runId)}&sessionId=${encodeURIComponent(sessionId)}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((act) => {
        if (act?.attempt?.walletMethod === 'paypal') {
          attemptIdRef.current = act.attempt.attemptId;
          setAttempt(act.attempt);
        }
      })
      .catch(() => {})
      .finally(() => setResolving(false));
  }, [runId, sessionId, loadConfig, isBag]);

  // Lock the page's other buy buttons once money may move: past the popup, or approved and
  // waiting on the courier phone. While PayPal's popup is merely open, Checkout stays usable.
  const locked = !!attempt?.locked && (attempt.phase !== 'authorizing' || needsPhone);

  useEffect(() => {
    onLockedChange?.(locked);
  }, [locked, onLockedChange]);

  useEffect(() => {
    onResolvingChange?.(resolving);
  }, [resolving, onResolvingChange]);

  const poll = useCallback(
    (attemptId: string, tries = 0) => {
      if (pollRef.current) clearTimeout(pollRef.current);
      pollRef.current = setTimeout(async () => {
        const r = await fetch(`/api/comix/wallet/status?attemptId=${encodeURIComponent(attemptId)}&sessionId=${encodeURIComponent(sessionId!)}`, { cache: 'no-store' })
          .then((x) => (x.ok ? x.json() : null))
          .catch(() => null);
        if (r?.attempt) setAttempt(r.attempt);
        const phase = r?.attempt?.phase;
        if ((r == null || WATCHED.includes(phase)) && tries < 90) poll(attemptId, tries + 1);
      }, tries < 10 ? 1000 : 3000);
    },
    [sessionId],
  );
  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  /** Server answer after approve / phone / cancel. */
  const handle = (json: any) => {
    const next: AttemptView | undefined = json?.attempt;
    if (next) setAttempt(next);
    if (json?.rejected) {
      setNeedsPhone(false);
      setMessage(json.rejected.message);
      return;
    }
    if (json?.needsPhone) {
      setNeedsPhone(true);
      setPhoneError(json.phoneError ?? null);
      return;
    }
    setNeedsPhone(false);
    if (next?.phase === 'not_charged') {
      setMessage(next.declineReason === 'declined' ? 'PayPal declined the payment. You weren’t charged.' : null);
      return;
    }
    if (next && WATCHED.includes(next.phase) && next.phase !== 'authorizing') poll(next.attemptId);
  };

  // Meta Purchase for internal wallet orders only (Shopify reports the rest).
  useEffect(() => {
    if (attempt?.phase === 'paid' && attempt.sitePurchaseEvent) walletMetaEvent('Purchase', attempt);
  }, [attempt?.phase, attempt?.attemptId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Amplitude: how a PayPal payment ended — once per attempt and outcome (also survives a
  // reload that restores the same attempt, via sessionStorage). Mirrors WalletExpressPay's
  // Apple/Google Pay tracking — without this, a PayPal decline/cancel was invisible: only
  // the 'paid' outcome used to be tracked here.
  useEffect(() => {
    if (!attempt) return;
    const outcome =
      attempt.phase === 'paid' ? 'paid'
      : attempt.phase === 'not_charged' ? attempt.declineReason ?? 'cancelled' // cancelled = popup closed / expired
      : attempt.phase === 'released' ? 'released'
      : attempt.phase === 'support' ? 'needs_support'
      : null;
    if (!outcome) return;
    const key = `pwm_wallet_result:${attempt.attemptId}:${outcome}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch { /* private mode: may repeat after a reload, never within the page */ }
    // Bag attempts carry per-line items (several books, possibly several copies each) — the
    // `quantity` prop is just this component's single-book default and doesn't reflect that.
    const qty = attempt.items?.length ? attempt.items.reduce((n, i) => n + i.quantity, 0) : quantity;
    trackEvent('Wallet - Payment Result', {
      outcome, // paid | cancelled | declined | card_not_supported | released | needs_support
      method: 'paypal',
      product: attempt.product,
      quantity: qty,
      totalCents: attempt.totalCents,
      placement: isBag ? 'bag' : 'unlock',
    });
  }, [attempt?.phase, attempt?.attemptId, attempt?.declineReason]); // eslint-disable-line react-hooks/exhaustive-deps

  // Paid → the same landing as the other wallets: the approve page.
  useEffect(() => {
    if (attempt?.phase !== 'paid') return;
    const ids = attempt.runIds?.length ? attempt.runIds : runId ? [runId] : [];
    if (!ids.length) return;
    try {
      for (const r of ids) sessionStorage.setItem(`comix_checkout_pending_${r}`, String(Date.now()));
      sessionStorage.setItem(
        'pwm_wallet_order_summary',
        JSON.stringify({ orderName: attempt.orderName, totalCents: attempt.totalCents, runIds: ids, method: 'paypal', items: attempt.items ?? [], at: Date.now() }),
      );
    } catch {
      /* private mode */
    }
    markCheckoutStarted();
    const target = ids.length === 1 ? `/comix/approve?runId=${encodeURIComponent(ids[0])}` : `/comix/order-complete?runIds=${ids.map(encodeURIComponent).join(',')}`;
    let cancelled = false;
    (async () => {
      if (attempt.product === 'bag') {
        // Remove exactly the paid lines from the bag before leaving (WalletExpressPay does the same).
        await post('/api/comix/wallet/bag-clear', { attemptId: attempt.attemptId, sessionId }).catch(() => null);
      }
      await new Promise((r) => setTimeout(r, 1000));
      if (!cancelled) window.location.href = target;
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt?.phase, attempt?.attemptId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!attemptIdRef.current) return;
    setBusy(true);
    setPhoneError(null);
    try {
      const r = await post('/api/comix/wallet/paypal-capture', { attemptId: attemptIdRef.current, sessionId, phone });
      handle(r.json);
    } finally {
      setBusy(false);
    }
  };

  if (!setup || !sessionId || (!runId && !isBag)) return null;
  const working = !!attempt && ['held', 'capturing', 'paid_order_pending', 'paid'].includes(attempt.phase);

  return (
    <div className={className}>
      {setup.env === 'sandbox' && (
        <p className="mb-2 rounded-xl bg-amber-50 px-3 py-2 text-center text-[16px] text-[#5c4300]">Test mode — PayPal sandbox, no real money.</p>
      )}
      <PayPalScriptProvider options={{ clientId: setup.clientId, currency: 'USD', intent: 'capture', components: 'buttons' }}>
        <PayPalButtons
          fundingSource="paypal"
          disabled={working || needsPhone || busy}
          forceReRender={[setup.clientId]}
          style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal', height: 52, borderRadius: 16, tagline: false }}
          createOrder={async () => {
            setMessage(null);
            setNeedsPhone(false);
            const r = isBag
              ? await post('/api/comix/wallet/paypal-create-bag', { sessionId, env: setup.env })
              : await post('/api/comix/wallet/paypal-create', {
                  runId,
                  sessionId,
                  product: productRef.current,
                  quantity: productRef.current === 'printed' ? quantityRef.current : 1,
                  pageCount: pageCountRef.current,
                  env: setup.env,
                });
            if (!r.ok || typeof r.json?.paypalOrderId !== 'string') {
              if (r.json?.error === 'paypal_env_changed') void loadConfig(); // reload the SDK for the right env
              else if (r.json?.error === 'payment_in_progress' && r.json.attempt) setAttempt(r.json.attempt);
              else setMessage('PayPal isn’t available right now. Please use Checkout.');
              throw new Error(r.json?.error || 'paypal_create_failed');
            }
            attemptIdRef.current = r.json.attempt.attemptId;
            setAttempt(r.json.attempt);
            walletMetaEvent('AddToCart', r.json.attempt);
            walletMetaEvent('InitiateCheckout', r.json.attempt);
            trackEvent('Checkout - Proceed', { product: productRef.current, source: 'wallet-paypal' });
            return r.json.paypalOrderId;
          }}
          // Reject a non-US / malformed ship-to inside the popup (only city/state/ZIP/country are shared here).
          onShippingAddressChange={async (data, actions) => {
            const r = await post('/api/comix/wallet/paypal-shipping', { sessionId, shippingAddress: data.shippingAddress }).catch(() => null);
            if (r && r.json?.ok === false) await actions.reject();
          }}
          onApprove={async () => {
            setBusy(true);
            try {
              const r = await post('/api/comix/wallet/paypal-capture', { attemptId: attemptIdRef.current, sessionId });
              if (r.json?.attempt || r.json?.rejected || r.json?.needsPhone) handle(r.json);
              else if (attemptIdRef.current) poll(attemptIdRef.current); // request failed — the server still knows
            } finally {
              setBusy(false);
            }
          }}
          onCancel={async () => {
            if (!attemptIdRef.current) return;
            const r = await post('/api/comix/wallet/paypal-cancel', { attemptId: attemptIdRef.current, sessionId }).catch(() => null);
            if (r) handle(r.json);
          }}
          onError={async () => {
            if (!attemptIdRef.current) return;
            const r = await post('/api/comix/wallet/paypal-cancel', { attemptId: attemptIdRef.current, sessionId }).catch(() => null);
            if (r) handle(r.json);
          }}
        />
      </PayPalScriptProvider>

      {needsPhone && (
        <form onSubmit={submitPhone} className="mt-3 flex flex-col gap-2 rounded-2xl border border-[#e0e0e0] p-4 text-[#333]">
          <label className="flex flex-col gap-1 text-[16px]">
            <span className="font-semibold">Phone for the courier</span>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              maxLength={LULU_ADDRESS_LIMITS.phone}
              placeholder="(212) 555-1234"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => setPhone((p) => formatUsPhone(p))}
              aria-invalid={!!phoneError}
              className={`h-[48px] rounded-[12px] border bg-white px-3 text-[16px] outline-none focus:border-[#F52F82] ${phoneError ? 'border-red-500' : 'border-[#d2d2d2]'}`}
            />
            {phoneError && <span className="text-[16px] text-red-600">{phoneError}</span>}
          </label>
          <button
            type="submit"
            disabled={busy || !phone.trim()}
            className="h-[52px] w-full rounded-2xl text-[18px] font-semibold text-white disabled:opacity-60"
            style={{ background: 'linear-gradient(90deg, #F52F82, #fd7468)' }}
          >
            {busy ? 'One moment…' : 'Complete payment'}
          </button>
        </form>
      )}

      {working && (
        <p className="mt-3 flex items-center justify-center gap-2 text-[16px] text-[#555]" role="status" aria-live="polite">
          <span className="h-5 w-5 animate-spin rounded-full border-[3px] border-[#F52F82]/30 border-t-[#F52F82]" aria-hidden="true" />
          {attempt?.phase === 'paid' ? 'Payment complete' : 'Finishing your payment…'}
        </p>
      )}
      {attempt?.phase === 'support' && (
        <p className="mt-3 text-center text-[16px] text-[#555]" role="status">We’re checking your payment. Don’t pay again — we’ll email you.</p>
      )}
      {message && (
        <p className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 p-3 text-[16px] text-[#5c4300]" role="alert">
          {message}
        </p>
      )}
    </div>
  );
}
